import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import OpenStaxPythonCodeRunnerBuilderNodeView from './open-stax-python-code-runner-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        openStaxPythonCodeRunnerBuilder: {
            insertOpenStaxPythonCodeRunnerBuilder: (options?: InsertOpenStaxPythonCodeRunnerBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertOpenStaxPythonCodeRunnerBuilderOptions {
    name?: string;
    id?: string;
    caption?: string;
    openStaxPythonCodeRunnerXMLToLoad?: string;
    options?: { [key: string]: any };
    challenge?: boolean;
}

export const OpenStaxPythonCodeRunnerBuilderNodeHeadless = Node.create({
    name: 'openStaxPythonCodeRunnerBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-open-stax-python-code-runner-builder-id'),
                renderHTML: attributes => ({
                    'data-open-stax-python-code-runner-builder-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            openStaxPythonCodeRunnerXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-open-stax-python-code-runner-xml') || '',
                renderHTML: attributes => ({
                    'data-open-stax-python-code-runner-xml': attributes.openStaxPythonCodeRunnerXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            instructions: {
                default: '',
                parseHTML: element => element.getAttribute('data-instructions') || '',
                renderHTML: attributes => ({
                    'data-instructions': attributes.instructions,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="open-stax-python-code-runner-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'open-stax-python-code-runner-builder',
                class: 'open-stax-python-code-runnerbuilder-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertOpenStaxPythonCodeRunnerBuilder:
                (options: InsertOpenStaxPythonCodeRunnerBuilderOptions = {}) =>
                ({ commands }) => {
                    const emptyOpenStaxPythonCodeRunnerXML = `<zyTool name="OpenStaxPythonCodeRunnerWrapper" caption="" parts="0" activity_type="" edited-last="Mon Feb 09 2026 14:37:07 GMT-0800 (Pacific Standard Time)"><zyOptions type="dict"><files type="list"><li type="dict"><contents type="string"></contents><name type="string">main.py</name><solution type="string"></solution></li></files><input type="string"></input><showSolution type="boolean">true</showSolution></zyOptions></zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            openStaxPythonCodeRunnerXMLToLoad:
                                options.openStaxPythonCodeRunnerXMLToLoad || emptyOpenStaxPythonCodeRunnerXML,
                            options: options.options || {},
                        },
                    });
                },
        };
    },
});

export const OpenStaxPythonCodeRunnerBuilderNode = OpenStaxPythonCodeRunnerBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(OpenStaxPythonCodeRunnerBuilderNodeView);
    },
});

export default OpenStaxPythonCodeRunnerBuilderNode;
